import { AppDebug, AppDebugLogType } from "../app/debug/AppDebug";
import { ScreenManager } from "../managers/ScreenManager";
import { TextureStatesData } from "../types/types";
import { GetBounds } from "../utils/GetBounds";
import { ButtonBase } from "./ButtonBase";


export class IconButton extends ButtonBase {
    // always visible icon
    public icon: Phaser.GameObjects.Image;

    // 
    protected iconTextureDatas: TextureStatesData;
    protected iconScale: number;

    /**
     * @example
     *  myButton = new IconButton(..)
     *  myButton.InitBase(..)
     *  myButton.InitIcon(..)
     *  myButton.PostInit(..)
     */
    constructor(scene: Phaser.Scene, x?: number, y?: number) {
        super(scene, x, y);
    }

    /**
     * Sets an additional image, to show or hide as button is selected/deselected
     * @param image 
     */
    public InitIcon(
        iconTextureDatas: TextureStatesData,
        iconPosition: Phaser.Geom.Point = null,
        iconScale: number = 1): void {
        this.iconTextureDatas = iconTextureDatas;
        this.iconScale = iconScale;

        const iconPos: Phaser.Geom.Point = iconPosition != null ? iconPosition : new Phaser.Geom.Point();
        this.icon = this.scene.add.image(iconPos.x, iconPos.y, iconTextureDatas.out.texture, iconTextureDatas.out.frame);
        this.icon.setScale(iconScale);
        this.add(this.icon);
    }

    public override PostInit(hitAreaExtrusion: number = 0): void {
        // > hitAreaExtrusion
        // > listeners (buttonTextureDatas)
        // > SetInteractive
        // > UpdateHitArea
        super.PostInit(hitAreaExtrusion);

        // > listeners (iconTextureDatas)
        if (this.buttonTextureDatas.over == null && this.iconTextureDatas.over != null) {

            this.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OVER, this.OnPointerOver);
            this.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OUT, this.OnPointerOut);
        }
    }

    protected override UpdateHitArea(): void {
        super.UpdateHitArea();

        const iconRect: Phaser.Geom.Rectangle = GetBounds(this.icon, this.hitAreaExtrusion);
        Phaser.Geom.Rectangle.Union(this.cachedHitArea, iconRect, this.cachedHitArea);

        if (this.input != null) {
            this.input.hitArea = this.cachedHitArea;
            this.input.hitAreaCallback = this.cachedHitAreaCallback;
        }
    }

    public SetIconTextureData(iconTextureDatas: TextureStatesData): void {
        this.iconTextureDatas = iconTextureDatas;
        // force; assume out state (?)
        this.icon.setFrame(iconTextureDatas.out.frame);
        this.UpdateViewFromInteractive();
    }

    /**
     * TODO: youll prob want to have 'disabled' icon state at least (maybe even 'selected'),
     *       so then youll need this override to apply it (and if you got 'selected' then also 'UpdateViewFromSelected')
     */
    protected override UpdateViewFromInteractive(): void {
        // toggle button texture view update
        super.UpdateViewFromInteractive();

        // was disabled, is enabled
        if (this.isInteractive) {
            // was using disabled texture
            if (this.iconTextureDatas.disabled != null) {
                // set 'out' texture
                this.icon.setTexture(this.iconTextureDatas.out.texture, this.iconTextureDatas.out.frame);
            }
        }
        // was enabled, is disabled
        else {
            // if got disabled texture
            if (this.iconTextureDatas.disabled != null) {
                // set disabled texture
                this.icon.setTexture(this.iconTextureDatas.disabled.texture, this.iconTextureDatas.disabled.frame);
            }
        }
    }

    protected override OnPointerOver() {
        // console.log("IconButton::OnPointerOver");

        super.OnPointerOver();

        if (this.isInteractive) {
            if (this.iconTextureDatas.over != null) {
                this.icon.setTexture(this.iconTextureDatas.over.texture, this.iconTextureDatas.over.frame);
            }
        }
    }

    protected override OnPointerOut() {
        // console.log("IconButton::OnPointerOut");

        super.OnPointerOut();

        if (this.isInteractive) {
            if (this.iconTextureDatas.out != null) {
                this.icon.setTexture(this.iconTextureDatas.out.texture, this.iconTextureDatas.out.frame);
            }
        }
    }

    public override destroy(): void {
        AppDebug.log("IconButton(" + this.name + ")::destroy", AppDebugLogType.DESTROY);

        super.destroy();

        this.icon.destroy();

        this.icon = null;
        this.iconTextureDatas = null;
    }
}